import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { GmailDeletionModel } from '../../../../types';
import { useUserSettings } from '../../../utils/use-user-settings';
import GmailDeletionToolbar from './gmail-deletion-toolbar';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useMutation } from '@tanstack/react-query';
import { runDeletionReport } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import GmailDeletionProcessDialog from './gmail-deletion-process-dialog';
import AvatarChip from '../../../shared/avatar-chip';
import dayjs from 'dayjs';
import DeleteGmailDeletionsDialog from './delete-gmail-deletions-dialog';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import { useNavigate } from 'react-router-dom';
import { pagesConfig } from '../../../shared/pages-config';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';

import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import DeleteSweep from '@mui/icons-material/DeleteSweep';
import Edit from '@mui/icons-material/Edit';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Unsubscribe from '@mui/icons-material/Unsubscribe';

interface GmailDeletionGridProps {
  gmailDeletionModels: GmailDeletionModel[];
  isLoading: boolean;
  isFetching: boolean;
  toolsRoles: string[];
  changeLogRoles: string[];
  loggedInUser: string;
  accessToken: string;
}

export default function GmailDeletionGrid({
  gmailDeletionModels,
  isLoading,
  isFetching,
  toolsRoles,
  changeLogRoles,
  loggedInUser,
  accessToken,
}: GmailDeletionGridProps) {
  const { createEvent } = useAnalytics();
  const navigate = useNavigate();
  const [openDeleteGmailDeletions, setOpenDeleteGmailDeletions] =
    useState(false);
  const [openGmailDeletionProcess, setOpenGmailDeletionProcess] =
    useState(false);
  const [selectedGmailDeletion, setSelectedGmailDeletion] =
    useState<GmailDeletionModel | null>(null);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [selectedGmailDeletions, setSelectedGmailDeletions] = useState<
    number[]
  >([]);
  const { mutate, isPending: isRunningReport } = useMutation({
    mutationFn: (data: GmailDeletionModel) =>
      runDeletionReport(data, changeLogMessage),
    onSuccess: (data) => {
      window.open(
        `https://docs.google.com/a/${
          loggedInUser.split('@')[1]
        }/spreadsheet/ccc?key=${data}#gid=0`,
        '_blank',
        'noopener, noreferrer'
      );
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue running the gmail deletion report. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.import_export.gmail_deletion.create_report, {
        module: ModuleList.modules.importExport.name,
        section: ModuleList.modules.importExport.sections.gmailDeletion,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const handleExportMutate = useCallback(
    (data: GmailDeletionModel) => {
      if (
        isAbleToSeeUIElement(changeLogRoles, ToolsRoles.ReportGmailSearch.value)
      ) {
        setOpenChangeLogDialog(true);
      } else {
        mutate(data);
      }
    },
    [changeLogRoles, mutate, setOpenChangeLogDialog]
  );

  function getNewSearchString(model: GmailDeletionModel) {
    let newString = '';
    if (model.from != null && model.from.length !== 0) {
      newString += 'from:(' + model.from + ') ';
    }
    if (model.to != null && model.to.length !== 0) {
      newString += 'to:(' + model.to + ') ';
    }
    if (model.subject != null && model.subject.length !== 0) {
      newString += 'subject:(' + model.subject + ') ';
    }
    if (
      model.hasTheWords != null &&
      model.hasTheWords.length !== 0 &&
      model.hasTheWords === ' '
    ) {
      newString += '(' + model.hasTheWords + ') ';
    }
    if (
      model.doesntHaveTheWords != null &&
      model.doesntHaveTheWords.length !== 0
    ) {
      newString += '-(' + model.doesntHaveTheWords + ') ';
    }
    if (model.whereToSearch != null && model.whereToSearch.length !== 0) {
      newString += model.whereToSearch + ' ';
    }
    return newString;
  }

  const columns: GridColDef<GmailDeletionModel>[] = useMemo(
    () => [
      {
        field: 'created',
        headerName: 'Created Date',
        width: 140,
        type: 'date',
        valueGetter: (_params, row) => {
          return new Date(row.created);
        },
        valueFormatter: (params: Date) => {
          const formattedDate = dayjs(params).format('M/D/YY, h:mm A');
          return formattedDate;
        },
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        flex: 1,
        renderCell: (params) => {
          return <AvatarChip email={params.value} type="User" />;
        },
      },
      {
        field: 'sources',
        headerName: 'Sources',
        flex: 1,
        display: 'flex',
        renderCell: (params) => {
          if (params.value) {
            return (
              <Stack flexDirection="row" gap={1} sx={{ overflowX: 'auto' }}>
                {params.value.map((target: string) => (
                  <AvatarChip
                    key={target}
                    email={target}
                    type={
                      params.row.sourceType === 'User'
                        ? 'User'
                        : params.row.sourceType === 'Group'
                        ? 'Group'
                        : 'Domain'
                    }
                  />
                ))}
              </Stack>
            );
          } else {
            return 'N/A';
          }
        },
      },
      {
        field: 'searchString',
        headerName: 'Search String',
        flex: 1,
        valueGetter: (_value, row: GmailDeletionModel) => {
          return getNewSearchString(row);
        },
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 100,
        type: 'singleSelect',
        valueOptions: [
          { label: 'By Message', value: 'message' },
          { label: 'By Thread', value: 'thread' },
        ],
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 150,
        getActions: (params) => {
          const buttonArray = [];
          buttonArray.push(
            <Tooltip
              arrow
              title={
                !isAbleToSeeUIElement(
                  toolsRoles,
                  ToolsRoles.ReportGmailSearch.value
                )
                  ? `You are missing the ${ToolsRoles.ReportGmailSearch.name} permission to perform this action.`
                  : 'Run Report'
              }
            >
              <span>
                <GridActionsCellItem
                  label="Run Report"
                  disabled={
                    !isAbleToSeeUIElement(
                      toolsRoles,
                      ToolsRoles.ReportGmailSearch.value
                    )
                  }
                  icon={
                    isRunningReport &&
                    selectedGmailDeletion?.created === params.row.created ? (
                      <CircularProgress size={24} />
                    ) : (
                      <PlayArrow />
                    )
                  }
                  onClick={() => {
                    setSelectedGmailDeletion(params.row);
                    handleExportMutate(params.row);
                  }}
                />
              </span>
            </Tooltip>
          );
          buttonArray.push(
            <Tooltip
              arrow
              title={
                !isAbleToSeeUIElement(
                  toolsRoles,
                  ToolsRoles.RunGmailSearch.value
                )
                  ? `You are missing the ${ToolsRoles.RunGmailSearch.name} permission to perform this action.`
                  : 'Run Deletion Process'
              }
            >
              <span>
                <GridActionsCellItem
                  label="Run Deletion Process"
                  disabled={
                    !isAbleToSeeUIElement(
                      toolsRoles,
                      ToolsRoles.RunGmailSearch.value
                    )
                  }
                  icon={<DeleteSweep />}
                  onClick={() => {
                    setSelectedGmailDeletion(params.row);
                    setOpenGmailDeletionProcess(true);
                  }}
                />
              </span>
            </Tooltip>
          );
          buttonArray.push(
            <Tooltip
              arrow
              title={
                !isAbleToSeeUIElement(
                  toolsRoles,
                  ToolsRoles.CreateGmailSearch.value
                )
                  ? `You are missing the ${ToolsRoles.CreateGmailSearch.name} permission to perform this action.`
                  : 'Edit'
              }
            >
              <span>
                <GridActionsCellItem
                  label="Edit"
                  icon={
                    <Tooltip arrow title="Edit">
                      <Edit />
                    </Tooltip>
                  }
                  disabled={
                    !isAbleToSeeUIElement(
                      toolsRoles,
                      ToolsRoles.CreateGmailSearch.value
                    )
                  }
                  onClick={() => {
                    navigate({
                      pathname: `/${
                        pagesConfig.gmailDeletion.link
                      }/${pagesConfig.editGmailDeletionFormPage.link.replace(
                        ':id',
                        params.row.created.toString()
                      )}`,
                    });
                  }}
                />
              </span>
            </Tooltip>
          );

          return buttonArray;
        },
      },
    ],
    [
      toolsRoles,
      isRunningReport,
      selectedGmailDeletion,
      handleExportMutate,
      setSelectedGmailDeletion,
      setOpenGmailDeletionProcess,
      navigate,
    ]
  );

  const { viewType } = useUserSettings();

  const defaultGridState = {
    sorting: {
      sortModel: [{ field: 'created', sort: 'desc' }],
    },
  };

  const toolbarProps = useMemo(
    () => ({
      toolsRoles,
      selectedGmailDeletions,
      setOpenDeleteGmailDeletions,
    }),
    [selectedGmailDeletions, toolsRoles]
  );

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: useToolbar(GmailDeletionToolbar, toolbarProps),
          ...createGridOverlays('Gmail Deletions', <Unsubscribe />),
        }}
        checkboxSelection
        getRowId={(row: GmailDeletionModel) => row.created}
        rows={gmailDeletionModels}
        columns={columns}
        defaultGridState={defaultGridState}
        gridKey={viewType.gmailDeletionsGridStates?.activeGridState}
        loading={isLoading || isFetching}
        selectedRows={selectedGmailDeletions}
        setSelectedRows={setSelectedGmailDeletions}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        gridStateKey="gmailDeletionsGridStates"
      />
      {selectedGmailDeletions.length > 0 && openDeleteGmailDeletions ? (
        <DeleteGmailDeletionsDialog
          open={openDeleteGmailDeletions}
          handleClose={() => setOpenDeleteGmailDeletions(false)}
          changeLogRoles={changeLogRoles}
          selectedModels={selectedGmailDeletions}
          setSelectedGmailDeletions={setSelectedGmailDeletions}
        />
      ) : null}
      {selectedGmailDeletion && openGmailDeletionProcess ? (
        <GmailDeletionProcessDialog
          open={openGmailDeletionProcess}
          handleClose={() => setOpenGmailDeletionProcess(false)}
          selectedModel={selectedGmailDeletion}
          changeLogRoles={changeLogRoles}
          loggedInUser={loggedInUser}
          accessToken={accessToken}
        />
      ) : null}
      {openChangeLogDialog ? (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            mutate(selectedGmailDeletion!);
          }}
          mutateLoading={isRunningReport}
        />
      ) : null}
    </>
  );
}
